import { Button } from "@blurb/codex-react";
import { AddIcon } from "@blurb/codex-react/icons";
import { GraphQL } from "@rpi/openapi-api";
import { Flex } from "@rpi/openapi-core";
import React from "react";
import { useOutlet } from "react-router-dom";
import { Pagination } from "../../../components/Pagination";
import { RpiTableContainer } from "../../../components/RpiTableContainer";
import { SearchFilterBar } from "../../../components/search-filter/SearchFilterBar";
import { SearchInput, toSearchInputArray } from "../../../configuration/filters/searchInput";
import { useAuthUser } from "../../../hooks/useAuthUser";
import { useNavigateRoute } from "../../../hooks/useNavigateRoute";
import { useRpiPagination } from "../../../hooks/usePagination";
import { RpiTable } from "../../../rpi-core/table/RpiTable";
import { SearchGeneral } from "../../../utils/graphql.util";
import { apiCredentialSearchLabels, apiCredentialSearchRenderers } from "../../../utils/searchField.util";
import { ApiCredentialsFilter } from "../../api-credentials/ApiCredentialsFilter";
import { apiKeysColumns, apiKeysMenuColumns } from "./apiKeysColumns";
import { WebhookSecretChip } from "./WebhookSecretChip";

export const ApiKeysTab: React.FC = () => {
  const user = useAuthUser();
  const navigateRoute = useNavigateRoute();
  const isCustomerRole = user.is.USER || user.is.CUSTOMER_ADMIN;

  const [searchInputs, setSearchInputs] = React.useState<SearchInput<GraphQL.ApiCredentialSearchFields>[]>([]);
  const [searchGeneral, setSearchGeneral] = React.useState<SearchGeneral<GraphQL.ApiCredentialsQueryVariables>>();
  const [isFilterOpen, setIsFilterOpen] = React.useState(false);

  const searchFilters = toSearchInputArray(searchInputs);
  const setSearchFilters = (
    recipe: (
      filters: SearchInput<GraphQL.ApiCredentialSearchFields>[]
    ) => SearchInput<GraphQL.ApiCredentialSearchFields>[]
  ) => {
    setSearchInputs((prev) => recipe(toSearchInputArray(prev)));
  };

  const sandboxFilter: GraphQL.ApiCredentialSearchInput = {
    field: GraphQL.ApiCredentialSearchFields.IsSandbox,
    condition: GraphQL.SearchCondition.Equals,
    firstValue: user.isSandbox.toString(),
  };
  const searchInputsWithSandboxFilter = [...searchInputs, sandboxFilter];

  const pagination = useRpiPagination({ perPage: 20 });

  const handleSearchSubmit = (searchValue: string) => {
    setSearchGeneral(searchValue || undefined);
    pagination.firstPage();
  };

  // No sort input: the table is unsortable per the design, so the backend's newest-first
  // default ordering stands.
  const { data, isLoading, error } = GraphQL.useApiCredentialsQuery(
    {
      limit: pagination.perPage,
      offset: pagination.offset,
      search: {
        inputs: searchInputsWithSandboxFilter,
        generalInput: searchGeneral,
      },
    },
    {
      onSuccess: (data) => pagination.setTotalCount(data.apiCredentials.totalCount),
    }
  );
  const apiCredentials = data?.apiCredentials.data;

  // No refetch when the drawer route closes — the ApiCredentials query is invalidated on
  // success, so refetching here would only duplicate that.
  const outlet = useOutlet();

  const paginationItemLabel = pagination.totalCount === 1 ? "item" : "items";

  return (
    <Flex.Column gap={16} pt={16}>
      {outlet}

      {isCustomerRole && (
        <Flex.Row justify="space-between" align="center" gap={16}>
          <WebhookSecretChip webhookSecret={apiCredentials?.[0]?.webhookSecret} />
          <Button
            variant="outlined"
            intent="primary"
            size="small"
            iconLeft={<AddIcon />}
            data-testid="createApiCredentialButton"
            onClick={() => navigateRoute(["developer-tools:api-keys:create"])}
          >
            Generate New Key
          </Button>
        </Flex.Row>
      )}

      {/* Search is super-admin only: customer roles always see their org's full key list,
          which also keeps the webhook secret chip (read from the first row) stable. */}
      {user.is.SUPER_ADMIN && (
        <SearchFilterBar
          placeholder="Search by API Key"
          isLoading={isLoading}
          filters={searchFilters}
          onSetFilters={setSearchFilters}
          onSearchSubmit={handleSearchSubmit}
          onFilterClick={() => setIsFilterOpen(true)}
          filterLabels={apiCredentialSearchLabels}
          valueRenderers={apiCredentialSearchRenderers}
        />
      )}

      <RpiTableContainer
        data-testid="api-credentials-table"
        footer={<Pagination isLoading={isLoading} pagination={pagination} label={paginationItemLabel} />}
      >
        <RpiTable
          emptyMessage={
            searchInputs.length || searchGeneral
              ? "No API credentials found."
              : "Your API credentials will appear here."
          }
          isLoading={isLoading}
          error={error}
          perPage={apiCredentials?.length}
          data={apiCredentials}
          columns={apiKeysColumns}
          additionalColumns={apiKeysMenuColumns}
        />
      </RpiTableContainer>

      {user.is.SUPER_ADMIN && (
        <ApiCredentialsFilter
          includeKeyStatus
          isOpened={isFilterOpen}
          onClose={() => setIsFilterOpen(false)}
          filters={searchFilters}
          onApply={setSearchInputs}
        />
      )}
    </Flex.Column>
  );
};
